if (!customElements.get('tdc-native-video')) {
  let instanceCount = 0;
  const modalPlayers = new Set();
  const updateScrollLock = () => document.documentElement.classList.toggle('tdc-native-video-mobile-open', modalPlayers.size > 0);

  customElements.define('tdc-native-video', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.closest('.product-form');
      this.bubble = this.querySelector('.tdc-native-video__bubble');
      this.preview = this.querySelector('.tdc-native-video__preview');
      this.previewImage = this.querySelector('.tdc-native-video__preview-image');
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('.tdc-native-video__player');
      this.image = this.querySelector('.tdc-native-video__image');
      this.launch = this.querySelector('.tdc-native-video__launch');
      this.toggle = this.querySelector('.tdc-native-video__toggle');
      this.mute = this.querySelector('.tdc-native-video__mute');
      this.seek = this.querySelector('.tdc-native-video__seek');
      this.time = this.querySelector('.tdc-native-video__time');
      this.options = this.querySelector('.tdc-native-video__options');
      this.menu = this.querySelector('.tdc-native-video__menu');
      this.notice = this.querySelector('.tdc-native-video__notice');
      this.error = this.querySelector('.tdc-native-video__error');
      this.navigation = this.querySelector('.tdc-native-video__navigation');
      this.count = this.querySelector('.tdc-native-video__count');
      this.fullscreen = this.querySelector('.tdc-native-video__fullscreen');
      try {
        const data = JSON.parse(this.dataset.videos || '[]');
        this.videos = Array.isArray(data) ? data.filter(video => video && this.validUrl(video.type === 'image' ? video.poster : video.player_src)).map(video => ({
          ...video,
          type: video.type === 'image' ? 'image' : 'video',
          preview_src: this.validUrl(video.preview_src) ? video.preview_src : video.player_src,
          poster: this.validUrl(video.poster) ? video.poster : ''
        })) : [];
      } catch {
        this.videos = [];
      }
      if (!this.form || !this.videos.length) return;
      this.index = 0;
      this.dismissed = false;
      this.dialog.id += `-${++instanceCount}`;
      this.menu.id += `-${instanceCount}`;
      this.launch.setAttribute('aria-controls', this.dialog.id);
      this.options.setAttribute('aria-controls', this.menu.id);
      this.player.controls = false;
      this.player.muted = true;
      this.setVideo(0);
      this.navigation.hidden = this.videos.length < 2;
      this.launch.addEventListener('click', () => this.open(), { signal });
      this.querySelector('.tdc-native-video__close').addEventListener('click', () => this.dialog.close(), { signal });
      this.querySelector('.tdc-native-video__dismiss').addEventListener('click', () => {
        this.dismissed = true;
        this.stopPreview();
        this.mountBubble();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.close();
      }, { signal });
      this.mobile = window.matchMedia('(max-width: 999px)');
      this.mobile.addEventListener('change', () => {
        if (!this.dialog.open) return;
        const focused = document.activeElement;
        this.dialog.close();
        this.present();
        if (this.dialog.contains(focused) && focused.getClientRects().length) focused.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        if (!this.menu.hidden) this.setMenu(false);
        else this.dialog.close();
      }, { signal });
      this.toggle.addEventListener('click', () => {
        if (this.player.paused) this.play();
        else this.player.pause();
      }, { signal });
      this.mute.addEventListener('click', () => { this.player.muted = !this.player.muted; }, { signal });
      this.options.addEventListener('click', () => this.setMenu(this.menu.hidden), { signal });
      this.dialog.addEventListener('click', event => {
        if (!this.menu.contains(event.target) && !this.options.contains(event.target)) this.setMenu(false);
      }, { signal });
      this.querySelector('.tdc-native-video__restart').addEventListener('click', () => {
        if (this.player.readyState > 0) this.player.currentTime = 0;
        this.setMenu(false);
        this.play();
        this.options.focus();
      }, { signal });
      this.fullscreen.addEventListener('click', () => {
        this.setMenu(false);
        const expanded = this.dialog.toggleAttribute('data-expanded');
        this.fullscreen.textContent = expanded ? this.fullscreen.dataset.reduceLabel : this.fullscreen.dataset.expandLabel;
        this.options.focus();
      }, { signal });
      this.querySelector('.tdc-native-video__share').addEventListener('click', event => this.copyLink(event.currentTarget), { signal });
      this.querySelector('.tdc-native-video__previous').addEventListener('click', () => this.changeVideo(-1), { signal });
      this.querySelector('.tdc-native-video__next').addEventListener('click', () => this.changeVideo(1), { signal });
      this.seek.addEventListener('input', () => {
        if (Number.isFinite(this.player.duration)) this.player.currentTime = Number(this.seek.value) / 100 * this.player.duration;
        this.updateProgress();
      }, { signal });
      this.player.addEventListener('error', () => { this.error.hidden = false; }, { signal });
      this.player.addEventListener('playing', () => { this.error.hidden = true; }, { signal });
      this.image.addEventListener('error', () => { if (this.isImage) this.error.hidden = false; }, { signal });
      this.image.addEventListener('load', () => { if (this.isImage) this.error.hidden = true; }, { signal });
      ['play', 'pause', 'volumechange', 'ended'].forEach(name => this.player.addEventListener(name, () => this.updateControls(), { signal }));
      ['timeupdate', 'durationchange', 'loadedmetadata'].forEach(name => this.player.addEventListener(name, () => this.updateProgress(), { signal }));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopPreview();
          this.player.pause();
        }
      }, { signal });
      window.addEventListener('pagehide', () => {
        this.stopPreview();
        this.player.pause();
      }, { signal });
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.stopPreview();
      }, { signal });
      this.connection = navigator.connection;
      this.connection?.addEventListener('change', () => {
        if (this.connection.saveData) this.stopPreview();
      }, { signal });
      this.previewObserver = new IntersectionObserver(entries => {
        this.inView = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.25;
        if (this.inView) this.schedulePreview();
        else this.stopPreview();
      }, { threshold: 0.25 });
      this.previewObserver.observe(this.bubble);
      this.anchorObserver = new MutationObserver(() => this.mountBubble());
      this.anchorObserver.observe(this.form, { childList: true, subtree: true });
      this.mountBubble();
      document.body.append(this.dialog);
      this.hidden = false;
      const ready = () => {
        this.pageReady = true;
        this.schedulePreview();
      };
      if (document.readyState === 'complete') ready();
      else window.addEventListener('load', ready, { once: true, signal });
      this.updateControls();
      this.updateProgress();
    }

    validUrl(value) {
      if (typeof value !== 'string' || !value) return false;
      try {
        const url = new URL(value, location.href);
        return url.protocol === 'https:' || url.protocol === 'http:';
      } catch {
        return false;
      }
    }

    mountBubble() {
      if (!this.form || !this.bubble) return;
      const quantity = this.form.querySelector('.product-form__quantity:has(quantity-selector)');
      const buttons = this.form.querySelector('.product-form__buy-buttons');
      const kitLayout = this.form.closest('.tdc-kit-cro');
      if (this.quantity && this.quantity !== quantity) this.quantity.classList.remove('tdc-native-video-quantity');
      this.quantity = quantity;
      if (kitLayout) {
        const anchor = quantity || buttons;
        if (anchor && (this.bubble.parentElement !== this.form || this.bubble.nextElementSibling !== anchor)) {
          this.form.insertBefore(this.bubble, anchor);
        }
      } else if (quantity && this.bubble.parentElement !== quantity) quantity.append(this.bubble);
      else if (!quantity && buttons && buttons.nextElementSibling !== this.bubble) buttons.after(this.bubble);
      const visible = !this.dismissed && Boolean(quantity || buttons);
      this.bubble.hidden = !visible;
      quantity?.classList.toggle('tdc-native-video-quantity', visible && !kitLayout);
      if (!visible) this.stopPreview();
    }

    setVideo(index) {
      this.stopPreview();
      this.player.pause();
      if (this.player.hasAttribute('src')) {
        this.player.removeAttribute('src');
        this.player.load();
      }
      if (this.preview.hasAttribute('src')) {
        this.preview.removeAttribute('src');
        this.preview.load();
      }
      this.index = index;
      const video = this.videos[index];
      this.isImage = video.type === 'image';
      this.preview.poster = video.poster;
      this.player.poster = video.poster;
      this.player.dataset.src = video.player_src;
      this.preview.dataset.src = video.preview_src;
      this.preview.hidden = this.isImage;
      this.previewImage.hidden = !this.isImage;
      this.image.hidden = !this.isImage;
      this.player.hidden = this.isImage;
      [this.toggle, this.mute, this.seek, this.time].forEach(control => { control.hidden = this.isImage; });
      this.bubble.toggleAttribute('data-image', this.isImage);
      this.dialog.toggleAttribute('data-image', this.isImage);
      if (this.isImage) {
        this.previewImage.src = video.poster;
        this.image.src = video.poster;
        this.image.alt = video.alt || this.dataset.productTitle || '';
      } else {
        this.previewImage.removeAttribute('src');
        this.image.removeAttribute('src');
      }
      this.error.hidden = true;
      this.dialog.removeAttribute('data-landscape');
      this.count.textContent = `${index + 1} / ${this.videos.length}`;
      this.seek.value = 0;
      this.seek.disabled = true;
      this.time.textContent = '0:00';
    }

    changeVideo(step) {
      this.setMenu(false);
      this.setVideo((this.index + step + this.videos.length) % this.videos.length);
      if (this.dialog.open) this.play();
    }

    schedulePreview() {
      if (!this.pageReady || !this.inView || this.previewPlayed || this.previewDelay) return;
      this.previewDelay = setTimeout(() => {
        this.previewDelay = null;
        this.playPreview();
      }, 1500);
    }

    playPreview() {
      if (!this.isConnected || this.dismissed || this.isImage || !this.inView || this.dialog.open || document.hidden || this.motion.matches || this.connection?.saveData) return;
      this.previewPlayed = true;
      this.preview.muted = true;
      this.preview.src = this.preview.dataset.src;
      this.preview.play().then(() => {
        if (!this.isConnected || this.dismissed || !this.inView || this.dialog.open || document.hidden || this.motion.matches || this.connection?.saveData) {
          this.preview.pause();
          return;
        }
        this.previewStop = setTimeout(() => this.preview.pause(), 5000);
      }).catch(() => {});
    }

    stopPreview() {
      clearTimeout(this.previewDelay);
      clearTimeout(this.previewStop);
      this.previewDelay = null;
      this.preview?.pause();
    }

    setMenu(open) {
      const focusedInside = this.menu.contains(document.activeElement);
      this.menu.hidden = !open;
      this.options.setAttribute('aria-expanded', String(open));
      if (!open && focusedInside) this.options.focus();
    }

    updateControls() {
      this.dialog.toggleAttribute('data-playing', !this.player.paused);
      this.dialog.toggleAttribute('data-muted', this.player.muted);
      this.toggle.setAttribute('aria-label', this.player.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.mute.setAttribute('aria-label', this.player.muted ? this.mute.dataset.unmuteLabel : this.mute.dataset.muteLabel);
    }

    updateProgress() {
      const duration = this.player.duration;
      const ready = Number.isFinite(duration) && duration > 0;
      const seconds = Math.floor(this.player.currentTime || 0);
      const format = value => `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
      this.seek.disabled = !ready;
      this.seek.value = ready ? this.player.currentTime / duration * 100 : 0;
      this.seek.style.setProperty('--tdc-native-video-progress', `${this.seek.value}%`);
      this.seek.setAttribute('aria-valuetext', ready ? `${format(seconds)} / ${format(Math.floor(duration))}` : '0:00');
      this.time.textContent = ready ? `${format(seconds)} / ${format(Math.floor(duration))}` : '0:00';
      this.dialog.toggleAttribute('data-landscape', this.player.videoWidth > this.player.videoHeight);
    }

    async copyLink(button) {
      let label;
      try {
        await navigator.clipboard.writeText(button.dataset.url);
        label = button.dataset.copiedLabel;
      } catch {
        label = button.dataset.failedLabel;
      }
      if (!this.isConnected) return;
      this.notice.textContent = label;
      this.notice.hidden = false;
      clearTimeout(this.noticeDelay);
      this.noticeDelay = setTimeout(() => { this.notice.hidden = true; }, 3000);
    }

    play() {
      if (this.isImage) return;
      if (!this.player.getAttribute('src') || this.player.error) this.player.src = this.player.dataset.src;
      this.player.play().catch(() => this.updateControls());
    }

    present() {
      this.dialog.setAttribute('aria-modal', String(this.mobile.matches));
      if (this.mobile.matches) {
        this.dialog.showModal();
        modalPlayers.add(this);
      } else {
        this.dialog.show();
        modalPlayers.delete(this);
      }
      updateScrollLock();
    }

    open() {
      if (this.dialog.open || this.dismissed) return;
      this.stopPreview();
      this.error.hidden = true;
      this.present();
      this.launch.setAttribute('aria-expanded', 'true');
      this.bubble.setAttribute('data-open', '');
      this.play();
    }

    close() {
      modalPlayers.delete(this);
      updateScrollLock();
      this.player.pause();
      this.setMenu(false);
      this.launch.setAttribute('aria-expanded', 'false');
      this.bubble.removeAttribute('data-open');
      this.notice.hidden = true;
      this.dialog.removeAttribute('data-expanded');
      this.fullscreen.textContent = this.fullscreen.dataset.expandLabel;
      if (this.isConnected && !this.dismissed && !this.bubble.hidden) this.launch.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.anchorObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.stopPreview();
      clearTimeout(this.noticeDelay);
      this.player?.pause();
      if (this.dialog?.open) this.dialog.close();
      modalPlayers.delete(this);
      updateScrollLock();
      this.quantity?.classList.remove('tdc-native-video-quantity');
      if (this.bubble) this.append(this.bubble);
      if (this.dialog) this.append(this.dialog);
      this.controller = null;
    }
  });
}

